import type { Timesheet, TimesheetEntry, Project, Employee } from '@/types/timesheet';
import { auditService } from '@/services/auditService';
import { addDays, startOfWeek, subWeeks, subDays, format } from 'date-fns';

let historicalTimesheetIdCounter = 10000;

const activityTypes = [
  'Client Meeting', 'Workshop', 'Stand-up', 'Stakeholder Meeting',
  'Development', 'Configuration', 'Build', 'Testing',
  'Documentation', 'Design Document', 'Runbook', 'User Guide',
  'Sprint Planning', 'Sprint Review', 'Team Meeting', 'Retrospective',
  'Research', 'Proof of Concept', 'Spike Work',
  'Travel', 'Client Site Visit', 'Code Review', 'Deployment',
  'Bug Fixing', 'Performance Tuning', 'Security Review'
];

const rejectionReasons = [
  'Please provide more detailed descriptions for your activities',
  'Hours exceed daily limit on Wednesday - please review',
  'Missing project code for client meeting entry',
  'Weekend hours require manager pre-approval',
  'Cost centre allocation incorrect - please use DEV for development work',
  'Duplicate entries detected - please consolidate',
  'Total hours don\'t match expected working hours',
  'Please split the large entry into more specific tasks'
];

const approverComments = [
  'Approved - good detail',
  'Approved',
  'Thanks for the detailed breakdown',
  'Approved - please ensure timely submission next week',
];

/**
 * Generate realistic timesheet entries for a given week
 */
const generateRealisticEntries = (
  projects: Project[],
  weekStart: Date,
  variation: 'normal' | 'light' | 'heavy' | 'weekend' = 'normal'
): TimesheetEntry[] => {
  const entries: TimesheetEntry[] = [];
  const numProjects = variation === 'light' ? 2 : Math.floor(Math.random() * 3) + 2; // 2-4 projects
  const selectedProjects = projects
    .filter(p => p.active)
    .sort(() => Math.random() - 0.5)
    .slice(0, numProjects);

  selectedProjects.forEach((project, index) => {
    const costCentre = project.costCentres[Math.floor(Math.random() * project.costCentres.length)];
    const activity = activityTypes[Math.floor(Math.random() * activityTypes.length)];
    
    // Base hours distribution
    let totalHours = 0;
    const hours = {
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0,
      sunday: 0
    };

    if (variation === 'light') {
      // Light week (vacation, sick leave)
      totalHours = index === 0 ? 15 : 7.5;
    } else if (variation === 'heavy') {
      // Heavy week (deadline, go-live)
      totalHours = index === 0 ? 45 : 15;
    } else if (variation === 'weekend') {
      // Weekend work
      totalHours = index === 0 ? 30 : 10;
      hours.saturday = 6;
      hours.sunday = 4;
    } else {
      // Normal week
      totalHours = index === 0 ? 30 : 7.5;
    }

    // Distribute across weekdays
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] as const;
    let remainingHours = totalHours - (hours.saturday + hours.sunday);
    
    // Standard 7.5 hour days
    for (let i = 0; i < 5 && remainingHours >= 7.5; i++) {
      hours[days[i]] = 7.5;
      remainingHours -= 7.5;
    }

    // Distribute remaining hours
    if (remainingHours > 0) {
      const day = days[Math.floor(Math.random() * 5)];
      hours[day] += remainingHours;
    }

    entries.push({
      id: -(entries.length + 1),
      projectId: project.id,
      costCentreId: costCentre.id,
      description: activity,
      hours
    });
  });

  return entries;
};

/**
 * Generate a complete timesheet lifecycle with audit trail
 */
const generateTimesheetWithHistory = (
  employee: Employee,
  weekStart: Date,
  projects: Project[],
  scenario: 'approved' | 'rejected-then-approved' | 'multiple-edits' | 'locked'
): Timesheet => {
  const weekEnding = addDays(weekStart, 6);
  const timesheetId = historicalTimesheetIdCounter++;
  
  // Initial creation (Monday of the week)
  const createdAt = addDays(weekStart, 0);
  const variation = Math.random() > 0.9 ? 'weekend' : Math.random() > 0.95 ? 'heavy' : 'normal';
  const initialEntries = generateRealisticEntries(projects, weekStart, variation);
  
  let timesheet: Timesheet = {
    id: timesheetId,
    employeeId: employee.id,
    employeeName: employee.name,
    weekEnding,
    status: 'draft',
    entries: initialEntries,
  };

  // Log creation
  auditService.logAction(
    timesheetId,
    'created',
    employee.id,
    employee.name,
    {
      newStatus: 'draft',
      actorRole: 'employee',
      timestamp: createdAt,
    }
  );

  if (scenario === 'approved') {
    // Simple flow: draft -> saved -> submitted -> approved
    
    // Save draft (Tuesday)
    const savedAt = addDays(weekStart, 1);
    auditService.logAction(
      timesheetId,
      'saved_draft',
      employee.id,
      employee.name,
      {
        newStatus: 'draft',
        actorRole: 'employee',
        timestamp: savedAt,
      }
    );

    // Submit (Friday)
    const submittedAt = addDays(weekStart, 4);
    timesheet.status = 'submitted';
    timesheet.submittedAt = submittedAt;
    auditService.logAction(
      timesheetId,
      'submitted',
      employee.id,
      employee.name,
      {
        previousStatus: 'draft',
        newStatus: 'submitted',
        actorRole: 'employee',
        timestamp: submittedAt,
      }
    );

    // Approve (Monday next week)
    const approvedAt = addDays(weekEnding, 2);
    const approverId = employee.managerId ?? 1;
    const approverName = 'Manager Name'; // In real app, look up from employees
    
    timesheet.status = 'approved';
    timesheet.approvedAt = approvedAt;
    timesheet.approverId = approverId;
    timesheet.approverName = approverName;
    
    auditService.logAction(
      timesheetId,
      'approved',
      approverId,
      approverName,
      {
        previousStatus: 'submitted',
        newStatus: 'approved',
        actorRole: 'manager',
        comment: approverComments[Math.floor(Math.random() * approverComments.length)],
        timestamp: approvedAt,
      }
    );

  } else if (scenario === 'rejected-then-approved') {
    // Complex flow: draft -> submitted -> rejected -> modified -> submitted -> approved
    
    // Submit (Friday)
    const submittedAt = addDays(weekStart, 4);
    timesheet.status = 'submitted';
    timesheet.submittedAt = submittedAt;
    auditService.logAction(
      timesheetId,
      'submitted',
      employee.id,
      employee.name,
      {
        previousStatus: 'draft',
        newStatus: 'submitted',
        actorRole: 'employee',
        timestamp: submittedAt,
      }
    );

    // Reject (Monday next week)
    const rejectedAt = addDays(weekEnding, 2);
    const approverId = employee.managerId ?? 1;
    const approverName = 'Manager Name';
    const rejectionReason = rejectionReasons[Math.floor(Math.random() * rejectionReasons.length)];
    
    timesheet.status = 'rejected';
    timesheet.rejectedAt = rejectedAt;
    timesheet.rejectionReason = rejectionReason;
    timesheet.approverId = approverId;
    timesheet.approverName = approverName;
    
    auditService.logAction(
      timesheetId,
      'rejected',
      approverId,
      approverName,
      {
        previousStatus: 'submitted',
        newStatus: 'rejected',
        actorRole: 'manager',
        comment: rejectionReason,
        timestamp: rejectedAt,
      }
    );

    // Modify (Tuesday next week)
    const modifiedAt = addDays(weekEnding, 3);
    const oldEntries = [...timesheet.entries];
    
    // Make some changes based on rejection reason
    if (rejectionReason.includes('description')) {
      timesheet.entries = timesheet.entries.map(entry => ({
        ...entry,
        description: `${entry.description} - Updated with more detail`
      }));
    } else if (rejectionReason.includes('hours')) {
      timesheet.entries = timesheet.entries.map(entry => ({
        ...entry,
        hours: {
          ...entry.hours,
          wednesday: Math.min(entry.hours.wednesday, 8)
        }
      }));
    }
    
    timesheet.status = 'draft';
    
    const changes = auditService.detectChanges(
      { ...timesheet, entries: oldEntries },
      timesheet
    );
    
    auditService.logAction(
      timesheetId,
      'modified',
      employee.id,
      employee.name,
      {
        previousStatus: 'rejected',
        newStatus: 'draft',
        actorRole: 'employee',
        changes,
        timestamp: modifiedAt,
      }
    );

    // Resubmit (Tuesday next week, later)
    const resubmittedAt = addDays(modifiedAt, 0);
    timesheet.status = 'submitted';
    timesheet.submittedAt = resubmittedAt;
    
    auditService.logAction(
      timesheetId,
      'submitted',
      employee.id,
      employee.name,
      {
        previousStatus: 'draft',
        newStatus: 'submitted',
        actorRole: 'employee',
        timestamp: resubmittedAt,
      }
    );

    // Approve (Wednesday next week)
    const approvedAt = addDays(weekEnding, 4);
    timesheet.status = 'approved';
    timesheet.approvedAt = approvedAt;
    
    auditService.logAction(
      timesheetId,
      'approved',
      approverId,
      approverName,
      {
        previousStatus: 'submitted',
        newStatus: 'approved',
        actorRole: 'manager',
        comment: 'Approved - thanks for the updates',
        timestamp: approvedAt,
      }
    );

  } else if (scenario === 'multiple-edits') {
    // Multiple edits before submission
    
    // Edit 1 (Tuesday)
    const edit1At = addDays(weekStart, 1);
    const oldEntries1 = [...timesheet.entries];
    timesheet.entries[0].hours.monday += 2;
    
    const changes1 = auditService.detectChanges(
      { ...timesheet, entries: oldEntries1 },
      timesheet
    );
    
    auditService.logAction(
      timesheetId,
      'modified',
      employee.id,
      employee.name,
      {
        newStatus: 'draft',
        actorRole: 'employee',
        changes: changes1,
        timestamp: edit1At,
      }
    );

    // Edit 2 (Wednesday)
    const edit2At = addDays(weekStart, 2);
    const oldEntries2 = [...timesheet.entries];
    timesheet.entries[0].description = `${timesheet.entries[0].description} - Sprint planning`;
    
    const changes2 = auditService.detectChanges(
      { ...timesheet, entries: oldEntries2 },
      timesheet
    );
    
    auditService.logAction(
      timesheetId,
      'modified',
      employee.id,
      employee.name,
      {
        newStatus: 'draft',
        actorRole: 'employee',
        changes: changes2,
        timestamp: edit2At,
      }
    );

    // Edit 3 (Thursday) - Add new entry
    const edit3At = addDays(weekStart, 3);
    const newEntry = generateRealisticEntries(projects, weekStart, 'light')[0];
    newEntry.hours = {
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 7.5,
      friday: 0,
      saturday: 0,
      sunday: 0
    };
    
    const oldEntries3 = [...timesheet.entries];
    timesheet.entries.push(newEntry);
    
    const changes3 = auditService.detectChanges(
      { ...timesheet, entries: oldEntries3 },
      timesheet
    );
    
    auditService.logAction(
      timesheetId,
      'modified',
      employee.id,
      employee.name,
      {
        newStatus: 'draft',
        actorRole: 'employee',
        changes: changes3,
        timestamp: edit3At,
      }
    );

    // Submit (Friday)
    const submittedAt = addDays(weekStart, 4);
    timesheet.status = 'submitted';
    timesheet.submittedAt = submittedAt;
    
    auditService.logAction(
      timesheetId,
      'submitted',
      employee.id,
      employee.name,
      {
        previousStatus: 'draft',
        newStatus: 'submitted',
        actorRole: 'employee',
        timestamp: submittedAt,
      }
    );

    // Approve (Monday next week)
    const approvedAt = addDays(weekEnding, 2);
    const approverId2 = employee.managerId ?? 1;
    const approverName = 'Manager Name';
    
    timesheet.status = 'approved';
    timesheet.approvedAt = approvedAt;
    timesheet.approverId = approverId2;
    timesheet.approverName = approverName;
    
    auditService.logAction(
      timesheetId,
      'approved',
      approverId2,
      approverName,
      {
        previousStatus: 'submitted',
        newStatus: 'approved',
        actorRole: 'manager',
        timestamp: approvedAt,
      }
    );

  } else if (scenario === 'locked') {
    // Approved and locked (payroll processed)
    
    // Submit (Friday)
    const submittedAt = addDays(weekStart, 4);
    timesheet.status = 'submitted';
    timesheet.submittedAt = submittedAt;
    
    auditService.logAction(
      timesheetId,
      'submitted',
      employee.id,
      employee.name,
      {
        previousStatus: 'draft',
        newStatus: 'submitted',
        actorRole: 'employee',
        timestamp: submittedAt,
      }
    );

    // Approve (Monday next week)
    const approvedAt = addDays(weekEnding, 2);
    const approverId3 = employee.managerId ?? 1;
    const approverName = 'Manager Name';
    
    timesheet.status = 'approved';
    timesheet.approvedAt = approvedAt;
    timesheet.approverId = approverId3;
    timesheet.approverName = approverName;
    
    auditService.logAction(
      timesheetId,
      'approved',
      approverId3,
      approverName,
      {
        previousStatus: 'submitted',
        newStatus: 'approved',
        actorRole: 'manager',
        timestamp: approvedAt,
      }
    );

    // Lock (Friday next week - payroll processed)
    const lockedAt = addDays(weekEnding, 6);
    timesheet.status = 'locked';
    
    auditService.logAction(
      timesheetId,
      'locked',
      'system',
      'Payroll System',
      {
        previousStatus: 'approved',
        newStatus: 'locked',
        actorRole: 'manager',
        comment: 'Locked for payroll processing',
        timestamp: lockedAt,
      }
    );
  }

  return timesheet;
};

/**
 * Generate historical timesheets with audit trails for the past 6 months
 */
export const generateHistoricalTimesheets = (
  employee: Employee,
  projects: Project[]
): Timesheet[] => {
  const timesheets: Timesheet[] = [];
  const currentWeek = startOfWeek(new Date(), { weekStartsOn: 1 });

  // Generate for past 24 weeks (6 months)
  for (let weekOffset = 1; weekOffset <= 24; weekOffset++) {
    const weekStart = subWeeks(currentWeek, weekOffset);
    
    // Determine scenario based on week
    let scenario: 'approved' | 'rejected-then-approved' | 'multiple-edits' | 'locked';
    
    if (weekOffset <= 2) {
      // Recent weeks: approved but not locked
      scenario = Math.random() > 0.7 ? 'multiple-edits' : 'approved';
    } else if (weekOffset <= 4) {
      // 2-4 weeks ago: some rejections
      scenario = Math.random() > 0.8 ? 'rejected-then-approved' : 'approved';
    } else {
      // Older weeks: locked
      scenario = 'locked';
    }

    const timesheet = generateTimesheetWithHistory(employee, weekStart, projects, scenario);
    timesheets.push(timesheet);
  }

  return timesheets;
};

/**
 * Initialize historical data for current user
 */
export const initializeHistoricalData = (
  currentEmployee: Employee,
  projects: Project[]
): void => {
  console.log('🕐 Generating historical timesheet data with audit trails...');
  
  const historicalTimesheets = generateHistoricalTimesheets(currentEmployee, projects);
  
  console.log(`✅ Generated ${historicalTimesheets.length} historical timesheets`);
  console.log(`📊 Audit entries created for complete lifecycle tracking`);
  
  // Return the timesheets so they can be added to the context
  return historicalTimesheets as any;
};

/**
 * Generate audit history for existing submitted timesheets
 * This is used to populate history for timesheets in the approval screen
 */
export const generateAuditHistoryForTimesheets = (
  timesheets: Timesheet[],
  employees: Employee[]
): void => {
  console.log('🕐 Generating audit history for submitted timesheets...');
  
  let count = 0;
  
  timesheets.forEach(timesheet => {
    // Only generate history for submitted, approved, or locked timesheets
    if (timesheet.status === 'draft') return;
    
    // Check if audit history already exists
    const existingHistory = auditService.getAuditTrail(timesheet.id);
    if (existingHistory.length > 0) return;
    
    const employee = employees.find(e => e.id === timesheet.employeeId);
    if (!employee) return;
    
    // Calculate dates based on week ending
    const weekStart = subDays(timesheet.weekEnding, 6);
    
    // 1. Created (Monday of the week)
    const createdAt = addDays(weekStart, 0);
    auditService.logAction(
      timesheet.id,
      'created',
      employee.id,
      employee.name,
      {
        newStatus: 'draft',
        actorRole: 'employee',
        timestamp: createdAt,
      }
    );
    
    // 2. Saved draft (Tuesday or Wednesday)
    const savedAt = addDays(weekStart, Math.random() > 0.5 ? 1 : 2);
    auditService.logAction(
      timesheet.id,
      'saved_draft',
      employee.id,
      employee.name,
      {
        newStatus: 'draft',
        actorRole: 'employee',
        timestamp: savedAt,
      }
    );
    
    // 3. Modified (if there are multiple entries, add a modification)
    if (timesheet.entries.length > 1) {
      const modifiedAt = addDays(weekStart, Math.random() > 0.5 ? 3 : 4);
      auditService.logAction(
        timesheet.id,
        'modified',
        employee.id,
        employee.name,
        {
          newStatus: 'draft',
          actorRole: 'employee',
          timestamp: modifiedAt,
          changes: [{
            field: 'entry_added',
            oldValue: null,
            newValue: {
              projectCode: timesheet.entries[1].projectId,
              description: timesheet.entries[1].description,
            },
            entryId: timesheet.entries[1].id,
          }],
        }
      );
    }
    
    // 4. Submitted (Friday or Monday after)
    const submittedAt = timesheet.submittedAt || addDays(weekStart, 4);
    auditService.logAction(
      timesheet.id,
      'submitted',
      employee.id,
      employee.name,
      {
        previousStatus: 'draft',
        newStatus: 'submitted',
        actorRole: 'employee',
        timestamp: submittedAt,
      }
    );
    
    // 5. Approved (if approved or locked)
    if (timesheet.status === 'approved' || timesheet.status === 'locked') {
      const approvedAt = timesheet.approvedAt || addDays(weekStart, 7);
      const approverId = timesheet.approverId ?? employee.managerId ?? 1;
      const approver = employees.find(e => e.id === approverId);
      const approverName = timesheet.approverName || approver?.name || 'Manager';
      
      auditService.logAction(
        timesheet.id,
        'approved',
        approverId,
        approverName,
        {
          previousStatus: 'submitted',
          newStatus: 'approved',
          actorRole: 'manager',
          comment: approverComments[Math.floor(Math.random() * approverComments.length)],
          timestamp: approvedAt,
        }
      );
    }
    
    // 6. Locked (if locked)
    if (timesheet.status === 'locked') {
      const lockedAt = addDays(weekStart, 14);
      auditService.logAction(
        timesheet.id,
        'locked',
        'system',
        'Payroll System',
        {
          previousStatus: 'approved',
          newStatus: 'locked',
          actorRole: 'manager',
          comment: 'Locked for payroll processing',
          timestamp: lockedAt,
        }
      );
    }
    
    count++;
  });
  
  console.log(`✅ Generated audit history for ${count} timesheets`);
};
